import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { DatePickerUS } from "../components/ui/date-picker-us";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Plus,
  Pencil,
  Trash2,
  Droplets,
  ArrowDownToLine,
  ArrowUpFromLine,
  AlertTriangle,
  History as HistoryIcon,
  Settings2,
  ShoppingBag,
} from "lucide-react";
import { fmtDateTime } from "../lib/format";
import { toast } from "sonner";
import { useConfirm } from "../hooks/useConfirm";
import { refreshSidebarCounts } from "../lib/sidebarBus";
import { FormField } from "../components/ui/form-field";
import { useI18n } from "../i18n/I18nContext";

const empty = {
  name: "",
  brand: "",
  category: "produto",
  unit: "un",
  current_stock: 0,
  min_stock: 0,
  status: "ativo",
  notes: "",
};

const CATS = {
  produto: { label: "Produto", color: "bg-blue-50 text-blue-700 border-blue-200" },
  quimico: {
    label: "Químico",
    color: "bg-amber-50 text-amber-800 border-amber-200",
  },
  descartavel: {
    label: "Descartável",
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  epi: { label: "EPI", color: "bg-rose-50 text-rose-700 border-rose-200" },
};

export default function Supplies() {
  const { t } = useI18n();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [moving, setMoving] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [resolving, setResolving] = useState(null);
  const [addQty, setAddQty] = useState("");

  const loadAlerts = useCallback(async () => {
    try {
      const { data } = await api.get("/supply-alerts?status=open");
      setAlerts(data || []);
    } catch {
      setAlerts([]);
    }
  }, []);

  const load = useCallback(async () => {
    const { data } = await api.get("/supplies");
    setItems(data);
  }, []);
  useEffect(() => {
    load();
    loadAlerts();
  }, [load, loadAlerts]);

  async function resolveAlert(alert, qty) {
    try {
      const { data } = await api.post(`/supply-alerts/${alert.id}/resolve`, {
        add_quantity: qty && Number(qty) > 0 ? Number(qty) : null,
      });
      toast.success(
        data?.new_stock != null
          ? `${t("Comprado e estoque atualizado")} (${data.new_stock})`
          : t("Marcado como comprado"),
      );
      setResolving(null);
      setAddQty("");
      loadAlerts();
      load();
      refreshSidebarCounts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function remove(s) {
    if (!(await confirm({
      title: `${t("Excluir")} "${s.name}"?`,
      description: t("Todo o histórico de movimentações deste item será apagado também. Esta ação não pode ser desfeita."),
      destructive: true,
      confirmText: t("Excluir"),
    }))) return;
    try {
      await api.delete(`/supplies/${s.id}`);
      toast.success(t("Item excluído"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  const lowCount = items.filter(
    (i) => Number(i.min_stock || 0) > 0 && Number(i.current_stock) <= Number(i.min_stock),
  ).length;

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Estoque")}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Droplets size={26} /> {t("Itens de limpeza")}
          </h1>
          <div className="text-sm text-slate-500 mt-1">
            {t("Cadastre os consumíveis (produtos, químicos, descartáveis, EPI) e controle o estoque")}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="supply-restock-btn"
            variant="outline"
            onClick={async () => {
              const { data } = await api.get("/supplies/restock-suggestions");
              setSuggestions(data);
            }}
            className="bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100"
          >
            <ShoppingBag size={16} className="mr-1" /> {t("Lista de reposição")}
          </Button>
          <Button
            data-testid="supply-new-btn"
            onClick={() => {
              setEditing({ ...empty });
              setOpen(true);
            }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus size={16} className="mr-1" /> {t("Novo item")}
          </Button>
        </div>
      </div>

      {lowCount > 0 && (
        <div className="cm-card p-3 mb-4 bg-amber-50 border-amber-200 flex items-center gap-2 text-amber-900 text-sm">
          <AlertTriangle size={16} />
          <strong>{lowCount}</strong> {t("item(ns) abaixo ou no estoque mínimo.")}
        </div>
      )}

      {alerts.length > 0 && (
        <div className="cm-card p-4 mb-4 border-rose-200" data-testid="supply-alerts-panel">
          <div className="flex items-center gap-2 mb-3">
            <ShoppingBag size={18} className="text-rose-600" />
            <h3 className="font-bold text-slate-900">
              {t("Avisos dos colaboradores")} ({alerts.length})
            </h3>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            {t("Materiais que os líderes avisaram que estão acabando ou acabaram. Marque como comprado após repor.")}
          </p>
          <ul className="space-y-2">
            {alerts.map((a) => (
              <li
                key={a.id}
                data-testid={`supply-alert-${a.id}`}
                className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-slate-200 p-3"
              >
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 flex items-center gap-2 flex-wrap">
                    {a.supply_name}
                    <span
                      className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${a.level === "out" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}
                    >
                      {a.level === "out" ? t("Acabou") : t("Acabando")}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {a.reported_by_name}{a.team_name ? ` • ${a.team_name}` : ""} • {fmtDateTime(a.created_at)}
                  </div>
                </div>
                <Button
                  data-testid={`supply-alert-resolve-${a.id}`}
                  onClick={() => { setResolving(a); setAddQty(""); }}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {t("Marcar como comprado")}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((s) => {
          const low =
            Number(s.min_stock || 0) > 0 &&
            Number(s.current_stock) <= Number(s.min_stock);
          const cat = CATS[s.category] || CATS.produto;
          return (
            <div
              key={s.id}
              data-testid={`supply-card-${s.id}`}
              className={`cm-card p-5 ${low ? "border-amber-300" : ""}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
                    <Droplets size={20} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                      {s.name}
                      {low && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 font-mono"
                          data-testid={`supply-low-${s.id}`}
                        >
                          {t("BAIXO")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {s.brand || "—"}
                    </div>
                    <span
                      className={`mt-1 inline-block text-[10px] px-2 py-0.5 rounded-full border ${cat.color}`}
                    >
                      {t(cat.label)}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => setHistoryOf(s)}
                    data-testid={`supply-history-${s.id}`}
                    className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                    title={t("Histórico")}
                  >
                    <HistoryIcon size={18} />
                  </button>
                  <button
                    onClick={() => {
                      setEditing(s);
                      setOpen(true);
                    }}
                    data-testid={`supply-edit-${s.id}`}
                    className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                    title={t("Editar")}
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    onClick={() => remove(s)}
                    data-testid={`supply-delete-${s.id}`}
                    className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                    title={t("Excluir")}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2 text-center">
                <div className={`p-2 rounded-lg ${low ? "bg-amber-50" : "bg-slate-50"}`}>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                    {t("Em estoque")}
                  </div>
                  <div
                    className={`text-2xl font-bold ${low ? "text-amber-700" : "text-slate-900"}`}
                  >
                    {s.current_stock} {s.unit}
                  </div>
                </div>
                <div className="p-2 rounded-lg bg-slate-50">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                    {t("Mínimo")}
                  </div>
                  <div className="text-2xl font-bold text-slate-700">
                    {s.min_stock} {s.unit}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-3">
                <Button
                  data-testid={`supply-in-${s.id}`}
                  variant="outline"
                  size="sm"
                  onClick={() => setMoving({ supply: s, type: "entrada" })}
                  className="min-h-[44px] sm:min-h-[36px] bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100"
                >
                  <ArrowDownToLine size={14} className="mr-1" /> {t("Entrada")}
                </Button>
                <Button
                  data-testid={`supply-out-${s.id}`}
                  variant="outline"
                  size="sm"
                  onClick={() => setMoving({ supply: s, type: "saida" })}
                  className="min-h-[44px] sm:min-h-[36px] bg-red-50 border-red-200 text-red-800 hover:bg-red-100"
                >
                  <ArrowUpFromLine size={14} className="mr-1" /> {t("Saída")}
                </Button>
                <Button
                  data-testid={`supply-adjust-${s.id}`}
                  variant="outline"
                  size="sm"
                  onClick={() => setMoving({ supply: s, type: "ajuste" })}
                  className="min-h-[44px] sm:min-h-[36px]"
                >
                  <Settings2 size={14} className="mr-1" /> {t("Ajuste")}
                </Button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="cm-card p-10 text-center text-slate-500 col-span-full">
            {t("Nenhum item cadastrado.")}
          </div>
        )}
      </div>

      <SupplyDialog
        open={open}
        onOpenChange={setOpen}
        item={editing}
        onSaved={() => {
          setOpen(false);
          load();
        }}
      />
      <MovementDialog
        moving={moving}
        onClose={() => setMoving(null)}
        onDone={() => {
          setMoving(null);
          load();
        }}
      />
      <SupplyHistoryDialog
        supply={historyOf}
        onClose={() => setHistoryOf(null)}
      />
      <RestockDialog
        suggestions={suggestions}
        onClose={() => setSuggestions(null)}
      />

      <Dialog open={!!resolving} onOpenChange={(v) => !v && setResolving(null)}>
        <DialogContent className="max-w-sm" data-testid="resolve-alert-dialog">
          <DialogHeader>
            <DialogTitle>{t("Marcar como comprado")}</DialogTitle>
          </DialogHeader>
          {resolving && (
            <div className="space-y-3">
              <div className="text-sm text-slate-600">
                {resolving.supply_name}
                {resolving.supply_unit ? ` (${resolving.supply_unit})` : ""}
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">
                  {t("Quantidade comprada (opcional)")}
                </label>
                <input
                  type="number"
                  min={0}
                  step="any"
                  data-testid="resolve-add-qty"
                  value={addQty}
                  onChange={(e) => setAddQty(e.target.value)}
                  placeholder="0"
                  className="cm-input mt-1"
                />
                <p className="text-xs text-slate-400 mt-1">
                  {t("Se informar, entra automaticamente no estoque. Deixe em branco para apenas marcar como comprado.")}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolving(null)}>
              {t("Cancelar")}
            </Button>
            <Button
              data-testid="resolve-confirm"
              onClick={() => resolveAlert(resolving, addQty)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {t("Confirmar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function RestockDialog({ suggestions, onClose }) {
  const { t } = useI18n();
  if (!suggestions) return null;
  const total = (suggestions || []).length;
  return (
    <Dialog open={!!suggestions} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-xl max-h-[85vh] overflow-y-auto"
        data-testid="restock-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingBag size={18} /> {t("Lista de reposição")}
          </DialogTitle>
        </DialogHeader>
        {total === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">
            🎉 {t("Todos os itens estão acima do estoque mínimo. Nenhuma reposição necessária.")}
          </div>
        ) : (
          <>
            <div className="text-sm text-slate-600 mb-3">
              <strong>{total}</strong> {t("item(ns) precisam de reposição. Sugestão calculada para repor até o dobro do estoque mínimo.")}
            </div>
            <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
              {suggestions.map((s) => (
                <div
                  key={s.supply_id}
                  data-testid={`restock-item-${s.supply_id}`}
                  className="flex items-center justify-between p-3 hover:bg-slate-50"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {s.name}{" "}
                      {s.brand && (
                        <span className="text-xs text-slate-500">
                          · {s.brand}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      {t("Atual:")} {s.current_stock} {s.unit} · {t("Mínimo:")}{" "}
                      {s.min_stock} {s.unit}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                      {t("Comprar")}
                    </div>
                    <div className="text-xl font-bold text-amber-700">
                      {s.suggested_quantity} {s.unit}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("Fechar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SupplyDialog({ open, onOpenChange, item, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(item || empty);
  useEffect(() => {
    setForm(item || empty);
  }, [item, open]);
  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  async function save() {
    try {
      const body = {
        ...form,
        current_stock: Number(form.current_stock) || 0,
        min_stock: Number(form.min_stock) || 0,
      };
      if (item?.id) await api.patch(`/supplies/${item.id}`, body);
      else await api.post("/supplies", body);
      toast.success(t("Item salvo"));
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="supply-dialog">
        <DialogHeader>
          <DialogTitle>
            {item?.id ? t("Editar item") : t("Novo item de limpeza")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <FormField label={t("Nome")}>
            
            <input
              data-testid="supply-field-name"
              className="cm-input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder={t("Ex: Detergente, Esponja, Pano de microfibra…")}
            />
</FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label={t("Marca")}>
              
              <input
                className="cm-input"
                value={form.brand || ""}
                onChange={(e) => set("brand", e.target.value)}
                placeholder={t("Ex: Lysol, Clorox…")}
              />
</FormField>
            <FormField label={t("Categoria")}>
              
              <select
                className="cm-input"
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
              >
                <option value="produto">{t("Produto")}</option>
                <option value="quimico">{t("Químico")}</option>
                <option value="descartavel">{t("Descartável")}</option>
                <option value="epi">EPI</option>
              </select>
</FormField>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <FormField label={t("Unidade")}>
              
              <input
                className="cm-input"
                value={form.unit}
                onChange={(e) => set("unit", e.target.value)}
                placeholder={t("un, ml, kg…")}
              />
</FormField>
            <FormField label={t("Estoque Inicial")}>
              
              <input
                data-testid="supply-field-stock"
                type="number"
                step="0.01"
                className="cm-input"
                value={form.current_stock}
                onChange={(e) => set("current_stock", e.target.value)}
              />
</FormField>
            <FormField label={t("Estoque Mínimo")}>
              
              <input
                data-testid="supply-field-min"
                type="number"
                step="0.01"
                className="cm-input"
                value={form.min_stock}
                onChange={(e) => set("min_stock", e.target.value)}
              />
</FormField>
          </div>
          <FormField label={t("Observações")}>
            
            <textarea
              rows={2}
              className="cm-input"
              value={form.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
            />
</FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("Cancelar")}
          </Button>
          <Button
            data-testid="supply-save-btn"
            onClick={save}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {t("Salvar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MovementDialog({ moving, onClose, onDone }) {
  const { t } = useI18n();
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [teamId, setTeamId] = useState("");
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (moving) {
      setQty("");
      setReason("");
      setTeamId("");
      api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
    }
  }, [moving]);
  if (!moving) return null;
  const { supply, type } = moving;
  const labels = {
    entrada: { title: t("Entrada de estoque"), color: "bg-emerald-600 hover:bg-emerald-700" },
    saida: { title: t("Saída / consumo"), color: "bg-red-600 hover:bg-red-700" },
    ajuste: { title: t("Ajuste de estoque"), color: "bg-slate-700 hover:bg-slate-800" },
  };
  const lab = labels[type];
  async function save() {
    if (!qty) return toast.error(t("Informe a quantidade"));
    setBusy(true);
    try {
      await api.post(`/supplies/${supply.id}/movements`, {
        type,
        quantity: Number(qty) || 0,
        reason: reason || undefined,
        team_id: teamId || undefined,
      });
      toast.success(t("Movimentação registrada"));
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={!!moving} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="supply-move-dialog">
        <DialogHeader>
          <DialogTitle>
            {lab.title} — {supply.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-slate-500">
            {t("Estoque atual:")}{" "}
            <strong className="text-slate-900">
              {supply.current_stock} {supply.unit}
            </strong>
          </div>
          <label className="block">
            <span className="block text-sm font-medium mb-1">
              {type === "ajuste"
                ? t("Estoque correto")
                : t("Quantidade ({unit})", { unit: supply.unit })}
            </span>
            <input
              data-testid="supply-move-qty"
              autoFocus
              type="number"
              step="0.01"
              min="0"
              className="cm-input text-xl text-center font-mono"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </label>
          {type === "saida" && (
            <FormField label={t("Equipe que Consumiu (opcional)")}>
              
              <select
                className="cm-input"
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
              >
                <option value="">{t("— escolher —")}</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
</FormField>
          )}
          <FormField label={t("Motivo / Observação")}>
            
            <input
              className="cm-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                type === "entrada"
                  ? t("Ex: Compra Walmart")
                  : type === "saida"
                    ? t("Ex: Usado na limpeza Anna Smith")
                    : t("Ex: Recontagem mensal")
              }
            />
</FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("Cancelar")}
          </Button>
          <Button
            data-testid="supply-move-confirm"
            onClick={save}
            disabled={busy}
            className={lab.color + " text-white"}
          >
            {t("Confirmar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SupplyHistoryDialog({ supply, onClose }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [teams, setTeams] = useState([]);
  const [filter, setFilter] = useState({ team_id: "", from: "", to: "" });
  useEffect(() => {
    if (!supply) return;
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
  }, [supply]);
  useEffect(() => {
    if (!supply) return;
    const qs = new URLSearchParams();
    if (filter.team_id) qs.append("team_id", filter.team_id);
    if (filter.from) qs.append("from", filter.from);
    if (filter.to) qs.append("to", filter.to);
    api
      .get(`/supplies/${supply.id}/movements?${qs.toString()}`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, [supply, filter]);
  if (!supply) return null;
  return (
    <Dialog open={!!supply} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-xl max-h-[85vh] overflow-y-auto"
        data-testid="supply-history-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HistoryIcon size={18} /> {t("Histórico")} — {supply.name}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-2 bg-slate-50 rounded-lg mb-2">
          <select
            className="cm-input text-sm"
            value={filter.team_id}
            onChange={(e) =>
              setFilter((f) => ({ ...f, team_id: e.target.value }))
            }
          >
            <option value="">{t("Todas as equipes")}</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <DatePickerUS
            value={filter.from}
            onChange={(v) => setFilter((f) => ({ ...f, from: v }))}
          />
          <DatePickerUS
            value={filter.to}
            onChange={(v) => setFilter((f) => ({ ...f, to: v }))}
          />
        </div>
        {items.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">
            {t("Nenhuma movimentação encontrada.")}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((m) => (
              <div
                key={m.id}
                className="py-3 flex items-start gap-3"
                data-testid={`supply-move-${m.id}`}
              >
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                    m.type === "entrada"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : m.type === "saida"
                        ? "bg-red-50 text-red-700 border border-red-200"
                        : "bg-slate-100 text-slate-700 border border-slate-200"
                  }`}
                >
                  {m.type === "entrada" ? "+" : m.type === "saida" ? "−" : "≈"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">
                    {m.type === "entrada"
                      ? t("+{qty} {unit} adicionados", { qty: m.quantity, unit: supply.unit })
                      : m.type === "saida"
                        ? (m.team_name
                            ? t("−{qty} {unit} consumido por {team}", { qty: m.quantity, unit: supply.unit, team: m.team_name })
                            : t("−{qty} {unit} consumido", { qty: m.quantity, unit: supply.unit }))
                        : t("Ajustado para {qty} {unit}", { qty: m.quantity, unit: supply.unit })}
                  </div>
                  <div className="text-xs text-slate-500">
                    {m.prev_stock} → {m.next_stock} · {t("por")} {m.user_name} ·{" "}
                    {fmtDateTime(m.created_at)}
                  </div>
                  {m.reason && (
                    <div className="text-xs text-slate-600 mt-0.5 italic">
                      "{m.reason}"
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("Fechar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
